import {CopyToClipboard} from '@macmillanplatform/ui-core';
import type {FiltersBarItem} from '@macmillanplatform/ui-core/types';
import {
  formatIsoDateToUtcMMDDYYYY,
  isObjectEmpty,
} from '@macmillanplatform/ui-core/utils';

import {
  ACCORDION_CONTENTS_KEYS,
  FALLBACK_VALUE,
  ORDERS_DETAILS_COLUMN_STYLES,
  ORDERS_DETAILS_FROZEN_COLUMNS,
  ORDERS_DETAILS_NOT_HIDABLE_COLUMNS,
  ORDERS_DETAILS_URL_PARAMS,
  ORDERS_URL_PARAMS,
} from '../../../constants';
import type {OrderDetailsItem} from '../../../types';

import {formatDateUniversal, withFallback} from '../../../helpers';
import {getSearchField} from '../../orders/helpers';
import {browserHistory} from '@/shared/utils';

import FilterStatus, {FilterStatusProps} from '../components/filter-status';
import SectionDetailsContent from '../components/section-details-content';
import SectionTitle, {SectionTitleItem} from '../components/section-title';

export const generateGridProps = () => {
  return {
    gridStyles: {width: '100%'},
    columnStyles: ORDERS_DETAILS_COLUMN_STYLES,
    frozenColumns: ORDERS_DETAILS_FROZEN_COLUMNS,
    notHidableColumns: ORDERS_DETAILS_NOT_HIDABLE_COLUMNS,
  };
};

export const getSections = (orderDetails: OrderDetailsItem[]) => {
  const actions = [
    {
      key: ACCORDION_CONTENTS_KEYS.DETAILS,
      children: (
        <div className="OrderDetails__accordion-action-details">
          <i className="fa-regular fa-list-ul OrderDetails__accordion-action-icon" />
          <span className="OrderDetails__accordion-action-text">
            Show Details
          </span>
        </div>
      ),
    },
  ];

  return orderDetails?.map((orderDetail: OrderDetailsItem) => {
    const trackingNumber = orderDetail?.clean_tracking_number;
    const trackingEvent = orderDetail?.shipment?.tracking_events?.[0];
    const shippingStatus = orderDetail?.shipping_status;

    const invoiceNumber = orderDetail?.invoice_number;

    const titleItems: SectionTitleItem[] = [
      {
        label: 'Invoice Number',
        value: withFallback(
          invoiceNumber && (
            <CopyToClipboard
              text={String(invoiceNumber)}
              tooltip="Click to copy invoice number"
            />
          )
        ),
        withoutTooltip: true,
      },
      {
        label: 'Shipper',
        value: withFallback(orderDetail?.carrier_description),
      },
      {
        label: 'Tracking Number',
        value: withFallback(
          trackingNumber && (
            <CopyToClipboard
              text={String(trackingNumber)}
              tooltip="Click to copy tracking number"
            />
          )
        ),
        withoutTooltip: true,
      },
      {label: 'Title', value: withFallback(orderDetail?.title)},
      {
        label: 'Last Status Change Date',
        value: formatDateUniversal(orderDetail?.carrier_updated_at, {
          formatter: formatIsoDateToUtcMMDDYYYY,
        }),
      },
      {
        label: 'Last Carrier Update',
        value: withFallback(trackingEvent?.status_message),
        valueBold: !!trackingEvent?.status_message,
        trackingStatus: trackingEvent?.status,
      },
    ];

    const detailsData = {
      firstRow: [
        {label: 'ISBN13', value: orderDetail?.isbn13},
        {label: 'Imprint', value: orderDetail?.imprint},
        {
          label: 'Shipped Units',
          value: orderDetail?.shipped_units,
          valueBold: true,
        },
        {
          label: 'Backorder Units',
          value: orderDetail?.backorder_units,
          valueBold: true,
        },
        {
          label: 'Canceled',
          value: orderDetail?.canceled_units,
          valueBold: true,
        },
      ],
      secondRow: [
        {label: 'Author', value: orderDetail?.author},
        {label: 'Format', value: orderDetail?.format},
        {label: 'Title Status', value: orderDetail?.title_status},
        {
          label: 'On Pick Units',
          value: orderDetail?.on_pick_units,
          valueBold: true,
        },
        {
          label: 'Credit Hold',
          value: orderDetail?.credit_hold_units,
          valueBold: true,
        },
      ],
    };

    const shipmentData = [
      {
        label: 'Tracking Date',
        value: formatDateUniversal(orderDetail?.tracking_date, {
          formatter: formatIsoDateToUtcMMDDYYYY,
        }),
        valueBold: true,
      },
      {
        label: 'Last Updated On',
        value: formatDateUniversal(
          orderDetail?.shipment?.tracking_events?.[0]?.timestamp
        ),
        valueBold: true,
      },
    ];

    const estimatedDeliveryDate = formatDateUniversal(
      orderDetail?.shipment?.estimated_delivery_start,
      {
        formatter: formatIsoDateToUtcMMDDYYYY,
      }
    );

    if (estimatedDeliveryDate !== FALLBACK_VALUE) {
      shipmentData.unshift({
        label: 'Estimated Delivery Date',
        value: formatDateUniversal(
          orderDetail?.shipment?.estimated_delivery_start,
          {
            formatter: formatIsoDateToUtcMMDDYYYY,
          }
        ),
        valueBold: true,
      });
    }

    return {
      title: (
        <SectionTitle items={titleItems} shippingStatus={shippingStatus} />
      ),
      actions,
      contents: {
        [ACCORDION_CONTENTS_KEYS.DETAILS]: () => (
          <SectionDetailsContent
            firstRow={detailsData.firstRow}
            secondRow={detailsData.secondRow}
            trackingEvents={orderDetail?.shipment?.tracking_events}
            shipmentUrl={orderDetail?.carrier_shipment_url}
            carrier={orderDetail?.shipment?.carrier}
            shipmentData={shipmentData}
          />
        ),
      },
    };
  });
};

const getExportField = ({exportFile, filename}): FiltersBarItem => ({
  type: 'download-button',
  name: ORDERS_DETAILS_URL_PARAMS.EXPORT_XLS,
  props: {
    filename,
    onDownloadDataLoad: exportFile,
    filetype: 'xlsx',
    label: 'Export',
    activeTooltipTitle: 'Export xlsx',
  },
});

export const getFiltersFields = ({exportFile, filename}) => {
  const name = ORDERS_DETAILS_URL_PARAMS.SEARCH;
  const searchField = getSearchField({
    name,
    placeholder: 'Search PO by ISBN or title',
    className: 'OrderDetails__field-search',
    customKey: name,
    clearable: true,
  });
  const exportField = getExportField({
    exportFile,
    filename,
  });

  return [searchField, exportField];
};

const customFilterField = (props: FilterStatusProps) => ({
  key: `custom-filter-${props.label}`,
  type: 'custom-filter',
  name: ORDERS_DETAILS_URL_PARAMS.SHIPPING_STATUS,
  component: <FilterStatus {...props} />,
});

const customFilterDivider = () => ({
  key: 'custom-filter-divider',
  type: 'custom-filter',
  name: `custom-filter-divider`,
  component: <div className="OrderDetails__filter-divider" />,
});

const getMultiSelectField = ({options, name, disabled}) => ({
  type: 'multiselect',
  name,
  props: {
    label: 'TITLE PROCESSING STATUS',
    options: options,
    onChange: (values: string[]) => {
      const selectedValues = values.join(',');
      const urlParams = browserHistory.getParametersObject();

      urlParams.set(name, selectedValues);
      urlParams.set(ORDERS_DETAILS_URL_PARAMS.OFFSET, '0');

      if (!selectedValues.length) urlParams.delete(name);

      browserHistory.update(urlParams);
    },
    disabled,
  },
});

export const getLeftFiltersFields = ({
  configPoStatuses,
  configShippingStatuses,
  selectedShippingStatus,
  shippingStatusCount = {},
  selectedPoStatuses,
  poStatusCount = {},
}) => {
  if (isObjectEmpty(shippingStatusCount)) return [];

  const hasPoSelection = selectedPoStatuses?.length > 0;
  const hasShippingSelection = selectedShippingStatus.length > 0;

  const shippingStatuses = configShippingStatuses.map(
    (status: {id: string; label: string}) =>
      customFilterField({
        id: status.id,
        label: status.label,
        count: shippingStatusCount[status.id],
        isSelected: selectedShippingStatus.includes(status.id),
        shippingStatus: selectedShippingStatus,
        disabled: hasPoSelection,
      })
  );

  const poStatusOptions = configPoStatuses.map(
    (status: {id: string; label: string}) => ({
      id: status.id,
      label: `${status.label} (${poStatusCount?.[status.id] || 0})`,
    })
  );

  const multiSelectField = getMultiSelectField({
    options: poStatusOptions,
    name: ORDERS_DETAILS_URL_PARAMS.PO_STATUS,
    disabled: hasShippingSelection,
  });

  const divider = customFilterDivider();

  return [multiSelectField, divider, ...shippingStatuses];
};

export const handlePagination =
  (urlParamsPrefix: string | undefined) => (limit: number, offset: number) => {
    const urlLimitName = urlParamsPrefix
      ? `${urlParamsPrefix}_${ORDERS_URL_PARAMS.LIMIT}`
      : ORDERS_URL_PARAMS.LIMIT;
    const urlOffsetName = urlParamsPrefix
      ? `${urlParamsPrefix}_${ORDERS_URL_PARAMS.OFFSET}`
      : ORDERS_URL_PARAMS.OFFSET;
    browserHistory.setParametersValues([
      {name: urlLimitName, value: limit},
      {name: urlOffsetName, value: offset},
    ]);
  };
